import { Box, Paper } from '@material-ui/core';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Route, Link, useRouteMatch, Redirect } from 'react-router-dom';
import styles from './ReferralsMenu.Styles';
import AdminReferralSentCountByDateDataModel from '../../../data-models/AdminReferralSentCountByDate.DataModel';
import AdminReferralTableDataModel from '../../../data-models/AdminReferralTableDataModel';
import VendorAdminTableDataModel from '../../../data-models/VendorAdminTable.DataModel';
import TenantEntity from '../../../entities/Tenant.Entity';
import UserEntity from '../../../entities/User.Entity';
import { getReferralsTable, getReferralsCountByDateTable } from '../../../services/Referrals.Service';
import { getTenants } from '../../../services/Tenants.Services';
import getProfile, { getUsers } from '../../../services/Users.Service';
import { getVendorAdminTable } from '../../../services/Vendors.Service';
import { BreadCrumb } from '../../../support/custom-hooks/useBreadCrum';
import { ReferralsCountByDate } from '../referrals-count-by-date/ReferralsCountByDate.Component';
import { Referrals, ReferralSearchFilter } from '../referrals/Referrals.Component';
import { Loader } from '../../generic-components/loader/Loader.Component';
import { ReportFilters, Reports } from '../reports/Reports.Component';
import { getReport } from '../../../services/Report.Service';
import ReportDataModel from '../../../data-models/Report.DataModel';
import UserAdminTableDataModel from '../../../data-models/UserAdminTableDataModel';
import ClientReportDataModel from '../../../data-models/ClientReport.DataModel';
import { ClientReports, ClientReportSearchFilter } from '../client-report/ClientReport.Component';
import { getClientReportsTable } from '../../../services/ClientReport.Service';
import AccessControl from '../../generic-components/access-control/AccessControl.Component';

import {
    generalReportRules,
    referralCountByDateRules,
    referralStatusRules,
    reportRules,
    clientReportRules,
} from '../../../support/constants/SecurityRules.Constants';

interface ReportsMenuState {
    loading: boolean;
    currentRoute: string;
    vendors: Array<VendorAdminTableDataModel>;
    tenants: Array<TenantEntity>;
    userProfile: UserEntity;
    referralsTable: Array<AdminReferralTableDataModel>;
    isReferralsStatusTableLoading: boolean;
    referralsCountByDateTable: Array<AdminReferralSentCountByDateDataModel>;
    isReferralsCountByDateTableLoading: boolean;
    reports: Array<ReportDataModel>;
    users: Array<UserAdminTableDataModel>;
    clientReportsTable: Array<ClientReportDataModel>;
    isClientReportsTableLoading: boolean;
}

interface ReportsProps {}

export const ReportsMenu: FunctionComponent<ReportsProps> = (props) => {
    const [state, setState] = useState<ReportsMenuState>({
        loading: false,
        currentRoute: null,
        vendors: null,
        tenants: null,
        userProfile: null,
        referralsTable: null,
        isReferralsStatusTableLoading: false,
        referralsCountByDateTable: null,
        isReferralsCountByDateTableLoading: false,
        reports: null,
        users: null,
        clientReportsTable: null,
        isClientReportsTableLoading: false,
    });

    const classes = styles();
    const { path } = useRouteMatch();

    const abortController = new AbortController();

    useEffect(() => {
        getUserProfile();
        setState({
            ...state,
            loading: true,
        });
    }, []);

    const getUserProfile = () => {
        setState({
            ...state,
            loading: true,
        });
        Promise.all([
            getTenants(abortController.signal),
            getVendorAdminTable(abortController.signal),
            getProfile(abortController.signal),
            getUsers(abortController.signal),
        ])
            .then((referralsTableResult) => {
                setState({
                    ...state,
                    currentRoute: 'referrals-info',
                    tenants: referralsTableResult[0],
                    vendors: referralsTableResult[1],
                    userProfile: referralsTableResult[2],
                    users: referralsTableResult[3],
                    loading: false,
                });
            })
            .catch(() => {
                setState({ ...state, loading: false });
            });
    };

    const handleGetReferralsTable = (filters: ReferralSearchFilter) => {
        setState({ ...state, isReferralsStatusTableLoading: true });

        getReferralsTable(filters, abortController.signal)
            .then((res) => {
                setState({ ...state, referralsTable: res, isReferralsStatusTableLoading: false });
            })
            .catch(() => {
                setState({ ...state, isReferralsStatusTableLoading: false });
            });
    };

    const handleGetReferralsCountByDateTable = (filters: ReferralSearchFilter) => {
        setState({ ...state, isReferralsCountByDateTableLoading: true });

        getReferralsCountByDateTable(filters, abortController.signal)
            .then((res) => {
                setState({ ...state, referralsCountByDateTable: res, isReferralsCountByDateTableLoading: false });
            })
            .catch(() => {
                setState({ ...state, isReferralsCountByDateTableLoading: false });
            });
    };

    const handleGetReport = (filters: ReportFilters) => {
        getReport(filters, abortController.signal).then((reports) => {
            setState({ ...state, reports: reports });
        });
    };

    const handleGetClientReportsTable = (filters: ClientReportSearchFilter) => {
        setState({ ...state, isClientReportsTableLoading: true });

        getClientReportsTable(filters, abortController.signal)
             .then((res) => {
                 setState({ ...state, clientReportsTable: res, isClientReportsTableLoading: false });
             })
             .catch(() => {
                 setState({ ...state, isClientReportsTableLoading: false });
             });
    };

    const referralsPermissions = [...reportRules.read, ...referralStatusRules.read];
    const referralsCountByDatePermissions = [...reportRules.read, ...referralCountByDateRules.read];
    const reportsPermissions = [...reportRules.read, ...generalReportRules.read];
    const clientReportReportsPermissions = [...reportRules.read, ...clientReportRules.read];
    
    const reportsBreadCrumb = {
        name: 'Reports',
        goBack: false,
        link: '/reports',
        path: path,
    };

    const referralStatusBreadCrumb = {
        name: 'Referral Status',
        goBack: false,
        link: '/reports/referral-status',
        path: path,
    };

    const referralCountByDateBreadCrumb = {
        name: 'Referral Count By Date',
        goBack: false,
        link: '/reports/referral-count-by-date',
        path: path,
    };

    const generalReportBreadCrumb = {
        name: 'General Report',
        goBack: false,
        link: '/reports/general-report',
        path: path,
    };

     const clientReportBreadCrumb = {
         name: 'Client Report',
         goBack: false,
         link: '/reports/client-report',
         path: path,
    };

    return state?.loading ? (
        <Loader isSmall />
    ) : (
        <>
            <Route path="/reports/referral-status" exact>
                <AccessControl
                    allowedPermissions={referralsPermissions}
                    sendBack={false}
                    renderNoAccess={<Redirect to="/not-found" />}
                >
                    <BreadCrumb sections={[reportsBreadCrumb, referralStatusBreadCrumb]} />
                    <Referrals
                        referrals={state.referralsTable}
                        tenants={state.tenants}
                        vendors={state.vendors}
                        handleGetReferralsTable={handleGetReferralsTable}
                        isLoading={state.isReferralsStatusTableLoading}
                    />
                </AccessControl>
            </Route>
            <Route path="/reports/referral-count-by-date" exact>
                <AccessControl
                    allowedPermissions={referralsCountByDatePermissions}
                    sendBack={false}
                    renderNoAccess={<Redirect to="/not-found" />}
                >
                    <BreadCrumb sections={[reportsBreadCrumb, referralCountByDateBreadCrumb]} />
                    <ReferralsCountByDate
                        referrals={state.referralsCountByDateTable}
                        tenants={state.tenants}
                        vendors={state.vendors}
                        handleGetReferralsTable={handleGetReferralsCountByDateTable}
                        isLoading={state.isReferralsCountByDateTableLoading}
                    />
                </AccessControl>
            </Route>
            <Route path="/reports/general-report" exact>
                <AccessControl
                    allowedPermissions={reportsPermissions}
                    sendBack={false}
                    renderNoAccess={<Redirect to="/not-found" />}
                >
                    <BreadCrumb sections={[reportsBreadCrumb, generalReportBreadCrumb]} />
                    <Reports
                        tenants={state.tenants}
                        vendors={state.vendors}
                        adjusters={state.users}
                        reportResults={state.reports}
                        handleGetReport={handleGetReport}
                        isLoading={state.isReferralsCountByDateTableLoading}
                    />
                </AccessControl>
            </Route>
            <Route path="/reports/client-report" exact>
                <AccessControl
                    allowedPermissions={clientReportReportsPermissions}
                    sendBack={false}
                    renderNoAccess={<Redirect to="/not-found" />}
                >
                    <BreadCrumb sections={[reportsBreadCrumb, clientReportBreadCrumb]} />
                    <ClientReports
                        tenants={state.tenants}
                        clientReports={state.clientReportsTable}
                        handleGetClientReportsTable={handleGetClientReportsTable}
                        isLoading={state.isClientReportsTableLoading}                        
                    />
                </AccessControl>
            </Route>

            <Route path="/reports" exact>
                <BreadCrumb sections={[reportsBreadCrumb]} />
                <Paper className={classes.paper}>
                    <Box className={classes.linksContainer}>
                        <AccessControl allowedPermissions={clientReportReportsPermissions} sendBack={false}>
                            <Link to={`${path}/client-report`} className={classes.link}>
                                Client Report
                            </Link>
                        </AccessControl>
                        <AccessControl allowedPermissions={reportsPermissions} sendBack={false}>
                            <Link to={`${path}/general-report`} className={classes.link}>
                                General Report
                            </Link>
                        </AccessControl>    
                        <AccessControl allowedPermissions={referralsCountByDatePermissions} sendBack={false}>
                            <Link to={`${path}/referral-count-by-date`} className={classes.link}>
                                Referral Count by date
                            </Link>
                        </AccessControl>
                        <AccessControl allowedPermissions={referralsPermissions} sendBack={false}>
                            <Link to={`${path}/referral-status`} className={classes.link}>
                                Referral Status
                            </Link>
                        </AccessControl>
                    </Box>
                </Paper>
            </Route>
        </>
    );
};
